import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  Typography,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { atom, useSetAtom } from 'jotai';
import AddIcon from '@mui/icons-material/Add';
import React, { useState, useCallback } from 'react';
import withServerDataGrid from '../../../Stateless/DataGrid';
import { useSnackbar } from '../../../Stateless/SnackBarProvider';
import EditStoreOwnerModal from '../../EditStoreOwnerModal';
import { COLORS } from '../../../../theme/themeConstants';
import AddBannerPage from './AddBannerPage';
import {
  useAllBanner,
  useDeleteBanner,
  usePatchBanner,
} from '../../../Query/BannerQuery';
import PermissionActionButtons from '../../../Stateless/Common/PermissionActionButtons';
import { usePermissions } from '../../../../context/PermissionContext';
import { usePermissionBasedColumns } from '../../../Stateless/Common/ColumnUtils';

export interface DynamicHomePageContentType {
  id?: string | number;
  imageUrl?: string;
  storeType?: string;
  imageTextEnglish?: string;
  imageTextArabic?: string;
  redirectUrl?: string;
  buttonTextEnglish?: string;
  buttonTextInArabic?: string;
  bannerStartTime?: string;
  bannerEndTime?: string;
  status?: string;
}
export const bannerAtom = atom<DynamicHomePageContentType | null>(null);

const ServerDataGrid = withServerDataGrid();

const BannerSettingsTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const { showMessage } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const snackbarMessage = ''; // Replace with actual message
  const snackbarSeverity: 'success' | 'error' | 'info' | 'warning' = 'info';
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [modalContent] = useState<'add' | 'edit'>('edit');
  const [searchQuery, setSearchQuery] = React.useState('');
  const { hasPermission } = usePermissions();
  const { getActionColumns } = usePermissionBasedColumns();

  type StoreWithSno = {
    id: string;
    sno: number;
    name?: string;
    merchant?: string;
    merchantEmail?: string;
    category?: string;
    addedBy?: string;
    status?: string;
  };

  const {
    data: settingsData,
    isLoading,
    setFilters: settingsDataFilter,
    query,
  } = useAllBanner({
    initialData: {
      page: 1,
      limit: 10,
      search: searchQuery,
      sortBy: 'createdAt',
      sortOrder: 'asc',
    },
  });


  const setBannerAtom = useSetAtom(bannerAtom);

  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchTerm(value);

      const updated = {
        search: value,
      };

      settingsDataFilter(updated);
    },
    []
  );

  const { mutate: patchBanner } = usePatchBanner();

  const { mutate: deleteBanner } = useDeleteBanner();

  const filteredRows = React.useMemo(() => {
    const storeOwners = settingsData?.data || [];
    const filtered = storeOwners.filter((row) => {
      const searchLower = searchQuery.toLowerCase();
      const storeType = (row.storeType || '').toLowerCase();
      return storeType.includes(searchLower);
    });

    // Add sno field to each row and ensure all required fields are present
    return filtered.map((row, index) => {
      // Ensure we have a valid row object
      if (!row) return { id: `empty-${index}`, sno: index + 1 } as StoreWithSno;

      // Create a new object with all the row properties plus sno
      const rowWithSno = {
        ...row,
        id: row.id,
        sno: index + 1 + (query.page - 1) * query.limit,
        storeType: row.storeType ?? 'N/A',
        imageUrl: row.imageUrl ?? 'N/A',
        imageTextEnglish: row.imageTextEnglish ?? 'N/A',
        // imageTextArabic: row.imageTextArabic ?? 'N/A',
        redirectUrl: row.redirectUrl ?? 'N/A',
        buttonTextEnglish: row.buttonTextEnglish ?? 'N/A',
        buttonTextInArabic: row.buttonTextInArabic ?? 'N/A',
        bannerStartTime: row.bannerStartTime ?? '',
        bannerEndTime: row.bannerEndTime ?? '',
        // status: row.status ?? 'N/A',
      };

      return rowWithSno as StoreWithSno;
    });
  }, [searchQuery, settingsData, query.page, query.limit, setSearchQuery]);

  const handleBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one banner to block', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to block?');
      if (!confirmed) return;

      patchBanner({
        id: selectedRows,
        data: { status: 'blocked' },
      });
    }
  };

  const handleUnBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one banner unblock', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to unblock?');
      if (!confirmed) return;

      patchBanner({
        id: selectedRows,
        data: { status: 'unblocked' },
      });
    }
  };

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one banner to delete', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteBanner(selectedRows);
      // deleteBanner(selectedRows, {
      // onSuccess: () => {
      //   setSelectedRows([]);
      //   showMessage('CMS deleted successfully', 'success');

      //   // Refetch data to update the table
      //   refetch();
      // },
      // onError: () => {
      //   showMessage('Error deleting CMS', 'error');
      //   // If there's an error, we might want to restore the selection
      //   // setSelectedRows(selectedRows); // Uncomment if you want to restore selection on error
      // },
      // });
    }
  }, [selectedRows]);

  const handleDeleteButton = useCallback(
    (id: string) => {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteBanner([id]);
    },
    [selectedRows, deleteBanner]
  );

  const handleAddSubAdmin = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleEditClick = (row: any) => {
    setBannerAtom(row);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setBannerAtom(null);
  };

  const handleBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to block this banner?'
    );
    if (!confirmed) return;

    await patchBanner({
      id: [id],
      data: { status: 'blocked' },
    });
  };
  const handleUnBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to unblock this banner?'
    );
    if (!confirmed) return;

    await patchBanner({
      id: [id],
      data: { status: 'unblocked' },
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'sno',
      headerName: 'S.No',
      flex: 0.3,
    },
    {
      field: 'imageUrl',
      headerName: 'Banner Image',
      flex: 2,
      renderCell: (params: GridRenderCellParams<any, string>) => {
        return params.value && params.value !== 'N/A' ? (
          <img
            src={params.value}
            alt="Banner"
            style={{ width: 100, height: 50, objectFit: 'contain' }}
          />
        ) : (
          <span>No Image</span>
        );
      },
    },
    {
      field: 'imageTextEnglish',
      headerName: 'Image Text (English)',
      flex: 1.5,
    },
    // {
    //   field: 'imageTextArabic',
    //   headerName: 'Image Text (Arabic)',
    //   flex: 1.5,
    // },
    {
      field: 'storeType',
      headerName: 'Store Type',
      flex: 1,
    },
    {
      field: 'bannerStartTime',
      headerName: 'Start Time',
      flex: 1,
    },
    {
      field: 'bannerEndTime',
      headerName: 'End Time',
      flex: 1,
    },
    // Add action columns conditionally based on permissions
    ...getActionColumns({
      module: 'settings',
      onEdit: handleEditClick,
      onDelete: (id) => handleDeleteButton(id),
      onBlock: handleBlockClickButton,
      onUnblock: handleUnBlockClickButton,
    }),
  ];

  // Format the manageStoreData for the DataGrid

  return (
    <>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        Banner Settings
      </Typography>
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          color: '#666',
        }}
      >
        {snackbarOpen && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            >
              {snackbarMessage}
            </Alert>
          </Box>
        )}
        <Box
          sx={{
            // p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {/* Search - Left */}
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search by store type"
              size="medium"
              value={searchTerm}
              onChange={handleSearch}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none',
                },
                '& fieldset': {
                  border: 'none',
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Action Buttons + Add Button - Right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <PermissionActionButtons
              module="settings"
              onBlock={handleBlockClick}
              onUnblock={handleUnBlockClick}
              onDelete={handleDeleteClick}
            />
            {hasPermission('settings', 'add') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSubAdmin}
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  bgcolor: COLORS.primary.main,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: COLORS.primary.main,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                Add Banner
              </Button>
            )}
          </Box>
        </Box>
        <EditStoreOwnerModal
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          title="Add Banner"
        >
          <AddBannerPage onCancel={handleCloseAddDialog} />
        </EditStoreOwnerModal>

        <EditStoreOwnerModal
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          title={modalContent === 'add' ? 'Add Banner' : 'Edit Banner'}
        >
          <AddBannerPage onCancel={handleCloseEditDialog} />
        </EditStoreOwnerModal>

        <ServerDataGrid
          rows={filteredRows || []}
          columns={columns}
          rowIdKey="id"
          loading={isLoading}
          totalItems={settingsData?.pagination?.totalItems || 0}
          page={currentPage}
          limit={pageLimit}
          pageSizeOptions={[10, 25, 50, 100]}
          selectedRows={selectedRows}
          onRowSelectionChange={(newSelection) => setSelectedRows(newSelection)}
          onPageChange={(page, limit) => {
            setCurrentPage(page);
            setPageLimit(limit);
            settingsDataFilter({ page, limit }); // fetch new data here
          }}
          onSortChange={(sortBy, sortOrder) =>
            settingsDataFilter({ sortBy, sortOrder })
          }
        />
      </Box>
    </>
  );
};

export default BannerSettingsTable;
